import { ENV_VALUES } from '@/config/env';
import { AwsAxiosConfig } from '@/node-repo/AwsAxiosConfig';
import { AuthUser, Credentials } from '@informme/shared/schemas/rbac/Rbac';
import type {
  AuthenticationResponseJSON,
  PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser';
import axios from 'axios';
import jwtDecode from 'jwt-decode';
import { Module, VuexModule } from 'vuex-class-modules';
import store from './store';

export const rolePermissions: { [role: string]: string[] } = {
  editor: ['/editor'],
  admin: ['/editor'],
  'operations-admin': ['/editor'],
  superadmin: ['/editor', '/admin', '/playground'],
};

@Module({ generateMutationSetters: true })
class AuthStore extends VuexModule {
  token = localStorage.getItem('token') || '';

  orgToken = localStorage.getItem('orgToken') || null;

  errorMessage = '';

  AUTH_API_BASE_URL = ENV_VALUES.VUE_APP_AUTH_API_BASE_URL;

  get user(): AuthUser | null {
    if (!this.token) return null;
    try {
      const payload = jwtDecode<AuthUser & { exp?: number }>(this.token);

      // Check if token is expired
      if (payload.exp) {
        const currentTime = Math.floor(Date.now() / 1000);
        if (payload.exp < currentTime) {
          // Token is expired, return null (handling will be done elsewhere)
          return null;
        }
      }

      // The token payload contains: username, roles, orgIds, personDetails
      return payload;
    } catch (error) {
      console.error('Error decoding token', error);
      // If decoding fails, token is invalid
      return null;
    }
  }

  get isTokenExpired(): boolean {
    if (!this.token) return true;
    try {
      const payload = jwtDecode<{ exp?: number }>(this.token);
      if (payload.exp) {
        const currentTime = Math.floor(Date.now() / 1000);
        return payload.exp < currentTime;
      }
      return false;
    } catch (error) {
      return true;
    }
  }

  get userLoggedIn() {
    return !!this.token;
  }

  get requestHeaders() {
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${this.token}`,
      'x-app-id': 'InformMe-Frontend-sy0JTUPvyjIiBpTe55KaqIriOaG',
    };
  }

  get orgRequestHeaders() {
    if (!this.orgToken) {
      return this.requestHeaders; // Fallback to user token if orgToken not set
    }
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${this.orgToken}`,
      'x-app-id': 'InformMe-Frontend-sy0JTUPvyjIiBpTe55KaqIriOaG',
    };
  }

  async logIn(credentials: Credentials): Promise<{
    mfaRequired: true;
    mfaToken: string;
    options: PublicKeyCredentialRequestOptionsJSON;
  } | null> {
    try {
      const { data } = await axios.post(`${this.AUTH_API_BASE_URL}/auth/login`, credentials);

      if (data?.mfaRequired) {
        this.errorMessage = '';
        return {
          mfaRequired: true,
          mfaToken: data.mfaToken,
          options: data.options,
        };
      }

      this.token = data?.token || '';
      this.errorMessage = '';

      // Store in localStorage
      if (this.token) {
        localStorage.setItem('token', this.token);
      }

      // User info is now extracted from token via the getter
      return null;
    } catch (error) {
      this.token = '';
      localStorage.removeItem('token');
      this.errorMessage = AwsAxiosConfig.getErrorMessageFromAxiosError(error);
      console.log('Error in logIn', error);
      return null;
    }
  }

  async completePasskeyLogin(input: {
    username: string;
    mfaToken: string;
    response: AuthenticationResponseJSON;
  }): Promise<void> {
    try {
      const { data } = await axios.post(`${this.AUTH_API_BASE_URL}/auth/passkey/login`, input);
      this.token = data?.token || '';
      this.errorMessage = '';
      if (this.token) {
        localStorage.setItem('token', this.token);
      }
    } catch (error) {
      this.token = '';
      localStorage.removeItem('token');
      this.errorMessage = AwsAxiosConfig.getErrorMessageFromAxiosError(error);
      console.log('Error in completePasskeyLogin', error);
    }
  }

  async getOrgToken(orgId: string): Promise<string> {
    try {
      const { data } = await axios.post(
        `${this.AUTH_API_BASE_URL}/token/get-org-token`,
        { orgId },
        { headers: this.requestHeaders },
      );

      this.orgToken = data?.token || '';
      if (this.orgToken) {
        localStorage.setItem('orgToken', this.orgToken);
      }
      if (!this.orgToken) {
        throw new Error('No org token received from server');
      }
      return this.orgToken;
    } catch (error) {
      console.error('Error getting org token', error);
      throw error;
    }
  }

  logOut() {
    this.token = '';
    this.orgToken = null;
    localStorage.removeItem('token');
    localStorage.removeItem('orgToken');
  }

  handleTokenExpiration() {
    // Use setTimeout to ensure this runs outside of Vuex mutation context
    setTimeout(() => {
      // Clear tokens
      this.logOut();

      // Redirect to login page if not already there
      if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
        const currentPath = window.location.pathname;
        window.location.href = `/login?to=${encodeURIComponent(currentPath)}`;
      }
    }, 0);
  }

  setErrorMessage(status: number): void {
    const selectMsg = (s: number): string => {
      switch (s) {
        case 401:
          return 'Incorrect username or password';
        case 403:
          return 'Access denied';
        case 500:
          return 'Server Error. Try again later';
        default:
          return 'Something went wrong. Try again later';
      }
    };

    this.errorMessage = selectMsg(status);
  }
}

const authStore = new AuthStore({ store, name: 'auth' });
export default authStore;
